import React from 'react';
import {
    Panel,
    PanelType,
    Dropdown,
    IDropdownOption,
    SpinButton,
    PrimaryButton,
    DefaultButton,
    Stack,
    ChoiceGroup,
    IChoiceGroupOption,
} from '@fluentui/react';
import { IDashboardWidget, IReportingWidget, IWidgetConfiguration, TimeframeType, getWidgetDisplayName, widgetHasConfiguration } from '../../../models/Dashboard';

interface IWidgetConfigPanelProps {
    isOpen: boolean;
    widget?: IDashboardWidget;
    widgetMeta?: IReportingWidget;
    onDismiss: () => void;
    onSave: (config: IWidgetConfiguration) => void;
}

const TIMEFRAME_OPTIONS: IDropdownOption[] = [
    { key: 'days', text: 'Last X Days' },
    { key: 'months', text: 'Last X Months' },
    { key: 'years', text: 'Last X Years' },
    { key: 'ytd', text: 'Year to Date' },
    { key: 'mtd', text: 'Month to Date' },
    { key: 'lastMonth', text: 'Last Month' },
];

const WidgetConfigPanel: React.FC<IWidgetConfigPanelProps> = ({ isOpen, widget, widgetMeta, onDismiss, onSave }) => {
    const [timeframe, setTimeframe] = React.useState<TimeframeType>(widget?.configuration?.timeframe || 'days');
    const [timeframeValue, setTimeframeValue] = React.useState<number>(widget?.configuration?.timeframeValue || 30);
    const [interval, setInterval] = React.useState<string | undefined>(widget?.configuration?.interval);
    const [metric, setMetric] = React.useState<string | undefined>(widget?.configuration?.metric);
    const [filter, setFilter] = React.useState<string | undefined>(widget?.configuration?.filter);

    React.useEffect(() => {
        const cfg = widget?.configuration;
        setTimeframe(cfg?.timeframe || 'days');
        setTimeframeValue(cfg?.timeframeValue || 30);
        setInterval(cfg?.interval);
        setMetric(cfg?.metric);
        setFilter(cfg?.filter);
    }, [widget]);

    if (!widgetMeta || !widget) return null;
    if (!widgetHasConfiguration(widgetMeta)) return null;

    const hasTimeframe = !!widgetMeta.hasTimeframeOptions;
    const intervalOptions: IChoiceGroupOption[] = (widgetMeta.supportedIntervals || []).map(i => ({ key: i, text: i }));
    const metricOptions: IChoiceGroupOption[] = (widgetMeta.metricOptions || []).map(m => ({ key: m, text: m }));
    const filterEntries = Object.entries(widgetMeta.filterOptions || {});

    const needsValue = ['days', 'months', 'years'].includes(timeframe);

    const handleSave = () => {
        const config: IWidgetConfiguration = {};
        if (hasTimeframe) {
            config.timeframe = timeframe;
            if (needsValue) config.timeframeValue = timeframeValue;
        }
        if (interval) config.interval = interval;
        if (metric) config.metric = metric;
        if (filter) config.filter = filter;
        onSave(config);
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            type={PanelType.smallFixedFar}
            headerText={`Configure: ${getWidgetDisplayName(widget.widgetType)}`}
            onRenderFooterContent={() => (
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <PrimaryButton text="Save" onClick={handleSave} />
                    <DefaultButton text="Cancel" onClick={onDismiss} />
                </Stack>
            )}
            isFooterAtBottom
        >
            <Stack tokens={{ childrenGap: 16 }} style={{ marginTop: 16 }}>
                {hasTimeframe && (
                    <>
                        <Dropdown
                            label="Timeframe"
                            selectedKey={timeframe}
                            options={TIMEFRAME_OPTIONS}
                            onChange={(_, option) => option && setTimeframe(option.key as TimeframeType)}
                        />
                        {needsValue && (
                            <SpinButton
                                label={`Number of ${timeframe}`}
                                value={timeframeValue.toString()}
                                min={1}
                                max={timeframe === 'days' ? 365 : timeframe === 'months' ? 60 : 10}
                                step={1}
                                onChange={(_, newVal) => newVal && setTimeframeValue(parseInt(newVal))}
                            />
                        )}
                    </>
                )}

                {intervalOptions.length > 0 && (
                    <ChoiceGroup
                        label="Interval"
                        selectedKey={interval}
                        options={intervalOptions}
                        onChange={(_, option) => option && setInterval(option.key)}
                    />
                )}

                {metricOptions.length > 0 && (
                    <ChoiceGroup
                        label="Metric"
                        selectedKey={metric}
                        options={metricOptions}
                        onChange={(_, option) => option && setMetric(option.key)}
                    />
                )}

                {filterEntries.map(([filterKey, values]) => (
                    <Dropdown
                        key={filterKey}
                        label={filterKey}
                        selectedKey={filter?.match(new RegExp(`(?:^|\\sand\\s)${filterKey}=([^\\s]+)`))?.[1]}
                        options={values.map(v => ({ key: v, text: v }))}
                        onChange={(_, option) => {
                            if (!option) return;
                            // Replace or insert "FilterKey=Value" in the filter string.
                            const entry = `${filterKey}=${option.key}`;
                            const regex = new RegExp(`(?:^|\\s+and\\s+)${filterKey}=[^\\s]+`);
                            if (filter && regex.test(filter)) {
                                setFilter(filter.replace(regex, (m) => m.startsWith(filterKey) ? entry : ` and ${entry}`));
                            } else {
                                setFilter(filter ? `${filter} and ${entry}` : entry);
                            }
                        }}
                    />
                ))}
            </Stack>
        </Panel>
    );
};

export default WidgetConfigPanel;
